import React from 'react';

import {Icon, ToggleButton, ErrorFilledSVG} from '@newsela/angelou';
import {actionNames, objectTypes, withTrackEvent} from '@newsela/snitch';
import PropTypes from 'prop-types';

import AiSparkWithGradientIcon from 'static/three-oh/src/components/GlobalLayout/GlobalLayoutV2/NavigationBar/AISearchMenuContainer/AiSparkWithGradientIcon/AiSparkWithGradientIcon.jsx';

import {
  innerContentCss,
  innerBorderCss,
  rootContainerCss,
  svgCss,
  textAreaRootContainerCss,
  labelTextCss,
  textAreaCss,
  headingSectionExpandedCss,
  toggleSwitchContainerCss,
  footerSectionCss,
  charLimitContainerCss,
  charLimitIconCss,
  charLimitInfoCss,
  charLimitCss,
  charLimitSlotCss,
} from './AIExpandableTextArea.styles';

const MAX_CHARS = 400;

const ToggleWithTrackEvent = withTrackEvent(ToggleButton);

const textAreaDescriptor = 'search-with-luna-textarea';

const AIExpandableTextArea = ({
  containerRef,
  expanded,
  handleExpandAndFocus,
  handleContainerBlur,
  isMobileDisplay,
  inputValue,
  setExpanded,
  setInputValue,
  textAreaRef,
  pressed,
  onToggle,
}) => {
  const isCharacterLimitReached = inputValue.length === MAX_CHARS;

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setExpanded(false);
    }

    // If Shift+Enter -> then no preventDefault, so user can create a newline
    if (e.key === 'Enter' && !e.shiftKey) {
      // Prevents textarea's native newline functionality so it can
      // submit the query instead.
      e.preventDefault();

      // TODO: Handle searching query here.

      // Collapse after submit
      setExpanded(false);
    }
  };

  const sparkleIcon = (
    <Icon
      SvgComponent={AiSparkWithGradientIcon}
      size={Icon.sizes.sm}
      isDecorative
      __cssFor={{root: svgCss}}
    />
  );

  return (
    <div
      ref={containerRef}
      tabIndex={-1}
      aria-expanded={expanded}
      aria-label="Search with Luna input"
      onFocus={handleExpandAndFocus}
      onBlur={handleContainerBlur}
      className={rootContainerCss(expanded, isMobileDisplay)}
      data-qa-selector="search-with-luna-toggle"
    >
      <div className={innerBorderCss(expanded, isMobileDisplay)}>
        <div className={innerContentCss(expanded)}>
          {expanded ? (
            <div className={headingSectionExpandedCss}>
              {sparkleIcon}
              <span>What are you looking for?</span>
            </div>
          ) : (
            sparkleIcon
          )}
          <div
            className={textAreaRootContainerCss(expanded, isCharacterLimitReached)}
          >
            <label htmlFor={textAreaDescriptor} className={labelTextCss}>
              Search with Luna
            </label>
            <textarea
              className={textAreaCss(expanded)}
              ref={textAreaRef}
              id={textAreaDescriptor}
              value={inputValue}
              maxLength={MAX_CHARS}
              placeholder={
                expanded
                  ? 'Find me a 7th grade article on the importance of recycling that connects to RI.7.5'
                  : 'What are you looking for?'
              }
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              data-qa-selector={textAreaDescriptor}
              name="needle"
              rows={expanded ? 7 : 1}
            />
          </div>
          {expanded ? (
            <div
              className={charLimitSlotCss(isCharacterLimitReached)}
              aria-live="polite"
            >
              <div className={charLimitContainerCss}>
                <Icon SvgComponent={ErrorFilledSVG} __cssFor={{root: charLimitIconCss}} />
                <div className={charLimitInfoCss}>
                  Character limit reached. Please shorten your text to search.
                </div>
              </div>
            </div>
          ) : null}
          {expanded ? (
            <div className={footerSectionCss}>
              <ToggleWithTrackEvent
                label="Disable Search with Luna"
                __cssFor={{
                  switchContainerStyles: pressed
                    ? toggleSwitchContainerCss
                    : '',
                }}
                dataProps={{
                  'data-qa-selector': 'disable-search-with-luna-toggle',
                }}
                objectType={objectTypes.user}
                actionName={actionNames.interact}
                streamProperties={{
                  description: 'Toggle Search with Luna',
                }}
                pressed={pressed}
                onClick={onToggle}
              />
              <div className={charLimitCss(isCharacterLimitReached)}>
                {inputValue.length}/{MAX_CHARS} characters
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

AIExpandableTextArea.propTypes = {
  containerRef: PropTypes.object,
  expanded: PropTypes.bool,
  handleExpandAndFocus: PropTypes.func,
  handleContainerBlur: PropTypes.func,
  isMobileDisplay: PropTypes.bool,
  inputValue: PropTypes.string,
  setExpanded: PropTypes.func,
  setInputValue: PropTypes.func,
  textAreaRef: PropTypes.object,
  pressed: PropTypes.bool,
  onToggle: PropTypes.func,
};
export default AIExpandableTextArea;
